const { parsePhoneNumberFromString } = require('libphonenumber-js/max');
const RUSSIAN_TRANSLITERATION = Object.freeze({
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya'
});
function transliterateSlug(value) {
  return String(value || '').normalize('NFKC').toLowerCase().split('')
    .map((character) => RUSSIAN_TRANSLITERATION[character] ?? character).join('')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-').slice(0, 50).replace(/-+$/g, '');
}
function isSafeHttpUrl(value) {
  if (typeof value !== 'string') return false;
  const candidate = value.trim();
  if (!candidate || candidate.length > 2048 || /[\u0000-\u0020\u007f]/.test(candidate)) return false;
  try {
    const url = new URL(candidate);
    return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname) && url.username === '' && url.password === '';
  } catch (error) { return false; }
}
function getWebsiteDrafts(contact = {}) {
  contact = contact && typeof contact === 'object' ? contact : {};
  if (Array.isArray(contact.websites)) {
    return contact.websites.slice(0, 20).map((website) => ({
      name: typeof website?.name === 'string' ? website.name : '',
      url: typeof website?.url === 'string' ? website.url : ''
    }));
  }
  return typeof contact.website === 'string' && contact.website.trim()
    ? [{ name: 'Сайт', url: contact.website }]
    : [];
}
function normalizeWebsites(contact = {}) {
  const seen = new Set();
  return getWebsiteDrafts(contact)
    .map(({ name, url }) => ({ name: name.trim().slice(0, 100), url: url.trim().slice(0, 2048) }))
    .filter(({ url }) => isSafeHttpUrl(url))
    .filter(({ url }) => {
      const key = new URL(url).href;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ name, url }) => ({ name: name || 'Сайт', url }));
}
function normalizePhonePresentation(value) {
  return String(value || '')
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
    .replace(/[\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+/g, ' ')
    .replace(/[\u0660-\u0669]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[\u06F0-\u06F9]/g, (digit) => String(digit.charCodeAt(0) - 0x06F0))
    .replace(/\s+/g, ' ')
    .trim();
}
function normalizeRussianPhone(value) {
  if (typeof value !== 'string') return '';
  const raw = normalizePhonePresentation(value);
  if (!raw || !/^\+?[\d\s().-]+$/.test(raw)) return raw;

  const digits = raw.replace(/\D/g, '');
  const hasInternationalPrefix = raw.startsWith('+');
  let candidate = raw;

  // The RU field already shows +7. Normalize a domestic trunk prefix pasted
  // after it: +7 8 (999) ... becomes +7 (999) ... .
  if (digits.length === 12 && digits.startsWith('78')) {
    candidate = '+7' + digits.slice(2);
  } else if (!hasInternationalPrefix && digits.length === 11 && digits.startsWith('8')) {
    candidate = '+7' + digits.slice(1);
  } else if (digits.length === 11 && digits.startsWith('7')) {
    candidate = '+' + digits;
  } else if (!hasInternationalPrefix && digits.length === 10) {
    candidate = '+7' + digits;
  }

  // Keep partial input editable, but return complete valid Russian numbers in
  // one canonical form. libphonenumber-js remains the authority on RU validity.
  try {
    const phone = parsePhoneNumberFromString(candidate, 'RU');
    if (phone && phone.country === 'RU' && phone.isValid()) return phone.number;
  } catch (error) {}
  return candidate;
}
function isValidRussianPhone(value) {
  const normalized = normalizeRussianPhone(value);
  if (!normalized) return false;
  try {
    const phone = parsePhoneNumberFromString(normalized, 'RU');
    return Boolean(phone && phone.country === 'RU' && phone.isValid());
  } catch (error) { return false; }
}
function formatRussianPhone(value) {
  if (typeof value !== 'string' || !value.trim()) return '';
  const raw = value.trim();
  try {
    const phone = parsePhoneNumberFromString(normalizeRussianPhone(raw), 'RU');
    if (!phone || phone.country !== 'RU' || !phone.isValid()) return raw;
    return phone.formatNational().replace(/^8(?=[\s(])/, '+7');
  } catch (error) { return raw; }
}
function escapeVCardValue(value) {
  return String(value || '').replace(/\\/g, '\\\\').replace(/\r\n|\r|\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
}
function buildVCard(data = {}, publicUrl = '') {
  const personal = data.personal && typeof data.personal === 'object' ? data.personal : {};
  const contact = data.contact && typeof data.contact === 'object' ? data.contact : {};
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
  const firstName = String(personal.firstName || '').trim();
  const lastName = String(personal.lastName || '').trim();
  const fullName = (firstName + ' ' + lastName).trim();
  if (fullName) {
    lines.push('FN:' + escapeVCardValue(fullName));
    lines.push('N:' + escapeVCardValue(lastName) + ';' + escapeVCardValue(firstName) + ';;;');
  }
  if (personal.company) lines.push('ORG:' + escapeVCardValue(personal.company));
  if (personal.title) lines.push('TITLE:' + escapeVCardValue(personal.title));
  if (contact.phone) lines.push('TEL;TYPE=CELL:' + escapeVCardValue(contact.phone));
  if (contact.email) lines.push('EMAIL;TYPE=WORK:' + escapeVCardValue(contact.email));
  const uniqueUrls = new Set();
  for (const website of normalizeWebsites(contact)) {
    const key = new URL(website.url).href;
    if (uniqueUrls.has(key)) continue;
    uniqueUrls.add(key);
    lines.push('URL;TYPE=WORK:' + escapeVCardValue(website.url));
  }
  if (publicUrl && isSafeHttpUrl(publicUrl) && !uniqueUrls.has(new URL(publicUrl).href)) {
    lines.push('URL:' + escapeVCardValue(publicUrl));
  }
  if (personal.bio) lines.push('NOTE:' + escapeVCardValue(personal.bio));
  lines.push('END:VCARD');
  return lines.join('\r\n');
}
module.exports = { buildVCard, escapeVCardValue, formatRussianPhone, getWebsiteDrafts, isSafeHttpUrl, isValidRussianPhone, normalizeRussianPhone, normalizeWebsites, transliterateSlug };
